import 'mediaelement';
import hlsjs from 'hls.js';
import { possiblyShoutcast, formatShoutcast } from './audioFormats';
import loader from './hlsLoader';
import { proxyStreams } from './utils';

window.Hls = hlsjs;

const defaultParams = {
  bufferingTimeout: 20,
  rewindInterval: 30,
};

const formatSourcesAsShoutcast = (sources = []) => sources
  .map((source) => {
    const isPossiblyShoutcast = possiblyShoutcast(source.src);
    if (!isPossiblyShoutcast) return {};
    return { ...source, src: formatShoutcast(source.src) };
  })
  .filter(source => source.src);

class MediaService {
  successCallback(resolve) {
    return (media) => {
      const {
        onPlaying,
        onPause,
        onLoadMetaData,
        onError,
        onCanPlay,
        onProgress,
        onVolumeChange,
        onTimeUpdate,
        onEnded,
      } = this.callbacks;

      media.addEventListener('canplay', (event) => {
        clearTimeout(this.loadingAttempt);
        if (onCanPlay) onCanPlay(event);
      });
      if (onPlaying) media.addEventListener('playing', onPlaying);
      if (onProgress) media.addEventListener('progress', onProgress);
      if (onVolumeChange) media.addEventListener('volumechange', onVolumeChange);
      if (onTimeUpdate) media.addEventListener('timeupdate', onTimeUpdate);
      if (onEnded) media.addEventListener('ended', onEnded);

      if (onLoadMetaData) {
        media.addEventListener('loadedmetadata', onLoadMetaData);
        media.addEventListener(hlsjs.Events.MANIFEST_PARSED, onLoadMetaData);
      }

      if (onPause) {
        media.addEventListener('pause', (e) => {
          onPause(e);
        });
      }

      media.addEventListener('error', (error) => {
        const shoutcastSources = !this.isAttemptedLoadAsShoutcast
          && formatSourcesAsShoutcast(this.sources);
        if (shoutcastSources.length) {
          this.loadSrc(shoutcastSources, true);
          return;
        }
        if (onError) onError(error);
      });
      media.addEventListener(hlsjs.Events.ERROR, (error) => {
        // HLS JS has custom timeout and till then propagates error sequence
        if (error?.data.type === hlsjs.ErrorTypes.NETWORK_ERROR) clearTimeout(this.loadingAttempt);
      });

      resolve(media);
    };
  }

  // eslint-disable-next-line class-methods-use-this
  errorCallback(reject) {
    return error => reject(error);
  }

  constructor(callbacks = {}, id = 'player', params = {}) {
    const { MediaElementPlayer } = global;
    if (!MediaElementPlayer) throw new Error('Missing MediaElementPlayer');

    this.callbacks = callbacks;
    this.params = { ...defaultParams, ...params };
    this.isAttemptedLoadAsShoutcast = false;
    this.sources = [];

    this.initialization = new Promise((resolve, reject) => {
      this.player = new MediaElementPlayer(id, {
        pluginPath: 'mediaelement/build/',
        renderers: ['html5', 'native_hls'],
        clickToPlayPause: false,
        hls: {
          path: 'hls.js/dist/',
          loader,
        },
        success: this.successCallback(resolve),
        error: this.errorCallback(reject),
      });
    });
  }

  get muted() {
    return this.player?.muted;
  }

  get paused() {
    return this.player?.paused;
  }

  get duration() {
    return this.player?.duration || Infinity;
  }

  get currentTime() {
    return this.player?.currentTime || 0;
  }

  get ended() {
    return this.player?.ended;
  }

  async setSrc(src = []) {
    if (!this.player) return;
    await this.initialization;
    // should always keep originally passed sources
    // for proper error handling after player manipulates with URLs
    this.sources = src;
    this.destroyHls();
    // MediaElementPlayer doesn't react on empty array set as source
    const source = this.sources?.length ? proxyStreams(this.sources) : '';
    this.player.media.setSrc(source);
  }

  async load() {
    if (!this.player) return;
    await this.initialization;
    clearTimeout(this.loadingAttempt);
    this.player.media.load();
    const timeout = this.params.bufferingTimeout * 1000;
    if (this.callbacks.onError) {
      this.loadingAttempt = window.setTimeout(() =>
        this.callbacks.onError(new Error('Loading timeout')), timeout);
    }
  }

  async loadSrc(sources = [], tryAsShoutcast = false) {
    try {
      this.isAttemptedLoadAsShoutcast = tryAsShoutcast;
      // fixes unmute instability in Safari and iOS: resets "mute" tag when "src" is updated
      this.setMuted(false);
      await this.setSrc(sources);
      await this.load();
    } catch (error) {
      if (this.callbacks.onError) this.callbacks.onError(error);
    }
  }

  async play() {
    await this.initialization;
    this.player?.play();
  }

  async pause() {
    await this.initialization;
    this.player?.pause();
  }

  async setMuted(mute) {
    await this.initialization;
    this.player?.setMuted(mute);
  }

  async setVolume(volume) {
    await this.initialization;
    this.player?.setVolume(volume / 10);
  }

  async stop() {
    await this.initialization;
    const currentSrc = this.player.getSrc();
    // trick to stop buffering of stream on pause as pause() don't do it
    // and there is no stop() method for HTML audio
    this.setSrc();
    this.load();
    this.setSrc(currentSrc);
  }

  async rewind(direction) {
    await this.initialization;
    const time = this.currentTime + (this.params.rewindInterval * direction);
    this.player?.setCurrentTime(time);
  }

  async changePosition(time) {
    await this.initialization;
    this.player?.setCurrentTime(time);
  }

  purge() {
    this.destroyHls();
    this.player?.remove();
    this.player = null;
  }

  destroyHls() {
    this.player.media.hlsPlayer?.stopLoad();
    this.player.media.hlsPlayer?.destroy();
  }
}

export default MediaService;
